import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, Lock, Bell, ArrowRight } from "lucide-react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import { useRegion, isIndia } from "../contexts/RegionContext";
import { setIntent } from "../lib/nav";
import { TALENT_TRACKS, UPCOMING_TRACKS } from "../data/content";

const EnglishGate = () => (
    <div
        className="mb-10 p-4 rounded-xl flex items-start gap-3"
        style={{
            background: "linear-gradient(135deg, rgba(212,168,87,0.06), rgba(20,20,26,0.5))",
            border: "1px solid rgba(212,168,87,0.22)",
        }}
        data-testid="english-gate"
    >
        <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0 font-display text-base text-gold">
            En
        </div>
        <div className="flex-1 text-sm leading-snug">
            <span className="text-gold font-medium">English fluency is required</span>
            <span className="text-ivory/70"> across every IndoBlitz track, internship and staff role. We do not teach English - please come prepared.</span>
        </div>
    </div>
);

const TrainingPage = () => {
    const { region } = useRegion();
    const inIndia = isIndia(region);

    return (
        <PageLayout title="Training & Internship · IndoBlitz" testId="training-page">
            <PageHero
                eyebrow="Training & Internship"
                title="Learn from practitioners,"
                accent="not theorists."
                intro="Paid, practical tracks in software, AI and workplace readiness - built for emerging talent in tier-2/3 India and motivated learners worldwide. Cohort discounts and rural / NGO sponsorships available."
                testId="training-hero"
            />

            <section className="relative pb-14 lg:pb-20" data-testid="training-tracks">
                <div className="max-w-5xl mx-auto px-5 lg:px-10">
                    <EnglishGate />

                    <div className="grid sm:grid-cols-3 gap-4">
                        {TALENT_TRACKS.map((t, i) => (
                            <motion.div
                                key={t.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="glass glass-hover rounded-2xl p-6 flex flex-col"
                                data-testid={`track-${i}`}
                            >
                                <div className="w-11 h-11 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                                    <GraduationCap className="w-5 h-5 text-gold" />
                                </div>
                                <h3 className="mt-5 font-display text-2xl text-ivory leading-tight">{t.name}</h3>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ivory/45">{t.duration}</div>
                                <div className="mt-4 font-display text-3xl text-gold">{inIndia ? t.india : t.global}</div>
                                <p className="mt-3 text-sm text-ivory/60 leading-relaxed flex-1">{t.for}</p>
                                <Link
                                    to="/contact"
                                    onClick={() => setIntent("enrol-track")}
                                    className="btn-ghost mt-5 !py-2.5 text-sm w-full justify-center"
                                    data-testid={`track-cta-${i}`}
                                >
                                    Enrol in this cohort <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {UPCOMING_TRACKS.length > 0 && (
                        <div className="mt-12">
                            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Opening soon</div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {UPCOMING_TRACKS.map((u) => (
                                    <Link
                                        key={u.id}
                                        to="/contact"
                                        onClick={() => setIntent(`${u.id}-interest`)}
                                        className="block rounded-2xl border border-dashed border-teal/30 bg-teal/[0.05] hover:border-teal/50 transition-colors p-6 group"
                                        data-testid={`upcoming-${u.id}`}
                                    >
                                        <div className="flex items-center gap-2 text-ivory font-display text-xl leading-tight">
                                            <Lock className="w-4 h-4 text-teal" /> {u.name}
                                        </div>
                                        <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-teal/80">{u.when} · {u.duration}</div>
                                        <p className="mt-3 text-sm text-ivory/60 leading-relaxed">{u.aligned}. {u.note}</p>
                                        <span className="mt-4 inline-flex items-center gap-1.5 text-teal text-xs font-mono uppercase tracking-widest">
                                            <Bell className="w-3 h-3" /> Notify me
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </PageLayout>
    );
};

export default TrainingPage;
